/* ============================================================
   screen-detail.jsx — Image detail (prompt / download / variations)
   ============================================================ */

import api from "./api/api.js";
import { normalizeImageItem, normalizeImageItems } from "./api/image-view-model.js";
import CONFIG from "./config/config.js";

function buildFullPrompt(item, lang) {
  if (!CONFIG.USE_MOCK) return (lang === "en" && item.promptEn ? item.promptEn : item.prompt) || "";
  const rnd = mulberry32((item.seed || 1) * 99991 >>> 0);
  const pick = (arr, n) => {
    const a = [...arr]; const out = [];
    for (let k = 0; k < n && a.length; k++) out.push(a.splice(Math.floor(rnd() * a.length), 1)[0]);
    return out;
  };
  const lighting = ["시네마틱 조명", "황금빛 역광", "부드러운 자연광", "드라마틱한 명암 대비", "은은한 림 라이트", "푸른 시간대의 빛"];
  const camera = ["85mm 망원 렌즈", "얕은 피사계 심도", "와이드 앵글 구도", "매크로 클로즈업", "로우 앵글 시점"];
  const detail = ["정교한 디테일", "섬세한 질감 묘사", "사실적인 재질감", "풍부한 색 계조", "선명한 윤곽선"];
  const mood = ["몽환적인 분위기", "고요하고 정적인 무드", "생동감 넘치는 색감", "차분한 톤", "초현실적인 공기감"];
  const quality = ["초고해상도 8K", "프로페셔널 포토그래피", "아트스테이션 트렌딩", "고품질 렌더링", "디테일한 마감"];
  const D = PROMPT_DESC[lang] || PROMPT_DESC.ko;
  const base = lang === "en" && item.promptEn ? item.promptEn : item.prompt;
  const parts = [
    base,
    ...pick(D.lighting, 1),
    ...pick(D.camera, 1),
    ...pick(D.detail, 2),
    ...pick(D.mood, 1),
    ...pick(D.quality, 2),
  ];
  return parts.join(D.join) + D.end;
}

function Detail({ item, go, open, liked, saved, onLike, onSave, onDownload, startPrompt, toast, auth, onAdminDelete }) {
  if (!item) return null;
  const { lang, t } = useT();
  const isAdmin = auth?.role === "admin";
  const isLiked = liked.has(item.id) || item.liked;
  const isSaved = saved.has(item.id) || item.saved;
  const [copied, setCopied] = React.useState(false);
  const fallbackRelated = React.useMemo(() => {
    if (!CONFIG.USE_MOCK) return [];
    const base = GALLERY.filter((g) => g.id !== item.id && g.tags.some((t) => item.tags.includes(t))).slice(0, 6);
    if (base.length < 6) base.push(...GALLERY.filter((g) => g.id !== item.id && !base.includes(g)).slice(0, 6 - base.length));
    return base.map((image, index) => normalizeImageItem(image, index));
  }, [item.id, item.tags]);
  const [related, setRelated] = React.useState(fallbackRelated);
  React.useEffect(() => {
    setRelated(fallbackRelated);
    let cancelled = false;
    api.images.similar(item.id, { limit: 6 }).then((res) => {
      if (cancelled) return;
      const normalized = normalizeImageItems(res?.items || []);
      if (normalized.length) setRelated(normalized);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [item.id, fallbackRelated]);

  const fullPrompt = React.useMemo(() => buildFullPrompt(item, lang), [item.id, lang]);
  const seedText = lang === "en" && item.promptEn ? item.promptEn : item.prompt;
  const shareTargets = React.useMemo(() => ([
    { id: "gpt", label: "GPT", url: "https://chatgpt.com/", kind: "external" },
    { id: "claude", label: "Claude", url: "https://claude.ai/new", kind: "external" },
    { id: "gemini", label: "Gemini", url: "https://gemini.google.com/app", kind: "external" },
  ]), []);

  const ratioText = React.useMemo(() => {
    const width = Number(item.width);
    const height = Number(item.height);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return "—";
    const gcd = (a, b) => {
      let x = Math.abs(a);
      let y = Math.abs(b);
      while (y) [x, y] = [y, x % y];
      return x || 1;
    };
    const divisor = gcd(width, height);
    return `${width / divisor} X ${height / divisor}`;
  }, [item.width, item.height]);
  const sizeText = Number.isFinite(Number(item.width)) && Number.isFinite(Number(item.height)) && Number(item.width) > 0 && Number(item.height) > 0
    ? `${item.width} X ${item.height}`
    : "—";
  const meta = [
    [t("m_ar"), ratioText],
    [t("m_size"), sizeText],
    [t("m_resolution"), ""],
  ];

  const copyPrompt = () => {
    navigator.clipboard?.writeText(fullPrompt).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 1400);
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
      toast(t("t_link_copied"));
    } catch (e) {
      toast(t("t_link_copied"));
    }
  };

  const openShareTarget = (target) => {
    if (target.kind === "internal") {
      startPrompt(seedText);
      return;
    }
    window.open(target.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div>
      {/* floating back — pinned in the left gutter on wide screens, follows scroll */}
      <button className="detail-back-float" onClick={() => go("gallery")} aria-label={t("back_gallery")}>
        <Icon name="chevronLeft" size={22} />
        <span className="detail-back-float-label">{t("back_gallery")}</span>
      </button>

      {/* breadcrumb */}
      <div className="container" style={{ padding: "18px 32px 0", display: "flex", alignItems: "center", gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => go("gallery")} style={{ paddingLeft: 8 }}>
          <Icon name="chevronLeft" size={18} /> {t("back_gallery")}
        </button>
      </div>

      <div className="container detail-grid" style={{ padding: "16px 32px 40px", display: "grid",
        gridTemplateColumns: "minmax(0, 1.5fr) minmax(320px, .85fr)", gap: 36, alignItems: "start" }}>
        {/* LEFT — image */}
        <div>
          <Reveal>
          <div style={{ borderRadius: "var(--r-lg)", overflow: "hidden", boxShadow: "var(--sh-3)",
            background: "var(--surface-3)" }}>
            <RemoteImage item={item} ratio={item.ratio || (item.ar === "1:1" ? "1 / 1" : item.ar === "3:4" ? "3 / 4" : item.ar === "16:9" ? "16 / 9" : "4 / 3")} radius="0" resolution="hi" fit="contain" loadingPlaceholder="skeleton" />
          </div>
          </Reveal>

          {/* generation info — under the image */}
          <div className="card" style={{ padding: "18px 20px", borderRadius: "var(--r-lg)", marginTop: 16 }}>
            <span style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 7, marginBottom: 14 }}>
              <Icon name="sliders" size={16} style={{ color: "var(--accent)" }} /> {t("gen_info")}
            </span>
            <dl style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px 20px", margin: 0 }}>
              {meta.map(([k, v]) => (
                <div key={k} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <dt className="muted" style={{ fontSize: 12 }}>{k}</dt>
                  <dd className="tnum" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* RIGHT — meta */}
        <aside style={{ position: "sticky", top: 84, display: "flex", flexDirection: "column", gap: 18 }}>
          {/* author + actions */}
          <div className="card" style={{ padding: 18, borderRadius: "var(--r-lg)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <Avatar author={item.author} size={42} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{authorName(item.author, lang)}</div>
                <div className="muted" style={{ fontSize: 13 }}>@{item.author.handle} · {t("days_ago", { n: item.createdDays })}</div>
              </div>
              {CONFIG.FEATURES.social && <button className="btn btn-line btn-sm">{t("follow")}</button>}
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              {CONFIG.FEATURES.generation && (
                <button className="btn btn-spark" onClick={() => startPrompt(seedText)} style={{ flex: 1 }}>
                  <Icon name="wand" size={17} stroke={2} /> {t("gen_from_prompt")}
                </button>
              )}
              {CONFIG.FEATURES.social && (
                <>
                  <button className="btn btn-line btn-icon" onClick={() => { onLike(item.id); }} aria-label={t("like")}
                    style={{ color: isLiked ? "var(--like)" : "var(--ink)" }}>
                    {isLiked ? <HeartSolid size={20} /> : <Icon name="heart" size={20} />}
                  </button>
                  <button className="btn btn-line btn-icon" onClick={() => onSave(item.id)} aria-label={t("save")}
                    style={{ color: isSaved ? "var(--accent)" : "var(--ink)" }}>
                    <Icon name="bookmark" size={20} />
                  </button>
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {CONFIG.FEATURES.download && (
                <button className="btn btn-ink" style={{ flex: 1 }} onClick={() => onDownload(item.id, { resolution: "hi" })}>
                  <Icon name="download" size={18} /> {t("download")}
                </button>
              )}
            </div>
            {isAdmin && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="btn btn-line" style={{ flex: 1, color: "var(--like)" }}
                  onClick={() => { if (window.confirm(t("admin_delete_confirm"))) onAdminDelete(item.id); }}>
                  <Icon name="trash" size={18} /> {t("admin_delete")}
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: 18, marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
              <Stat icon="heart" label={t("stat_likes")} value={item.likes} />
              <Stat icon="eye" label={t("stat_views")} value={item.views} />
              <Stat icon="download" label={t("stat_saves")} value={item.saves || 0} />
            </div>
          </div>

          {/* prompt */}
          <PromptCard
            title={t("prompt")}
            copyLabel={t("copy")}
            copiedLabel={t("copied")}
            copied={copied}
            prompt={fullPrompt}
            tags={item.tags}
            lang={lang}
            onCopy={copyPrompt}
          />

          {/* share */}
          <div className="card" style={{ padding: 18, borderRadius: "var(--r-lg)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 13 }}>
              <span style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 7 }}>
                <Icon name="share" size={16} style={{ color: "var(--accent)" }} /> {t("share")}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={copyShareLink} style={{ height: 30 }}>
                <Icon name="copy" size={15} /> {t("copy")}
              </button>
            </div>
            <div className="detail-share-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
              {shareTargets.map((target) => (
                <button key={target.id} className="btn btn-line btn-sm detail-share-btn" onClick={() => openShareTarget(target)}
                  style={{ minWidth: 0, justifyContent: "center", paddingInline: 12 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{target.label}</span>
                  <Icon name="arrowUpRight" size={14} />
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* related */}
      <div className="container" style={{ padding: "8px 32px 72px" }}>
        <h2 style={{ fontSize: 24, marginBottom: 18 }}>{t("similar_works")}</h2>
        <div className="uniform-grid">
          {related.map((it, i) => (
            <ImageCard key={it.id} item={it} index={i} layout="uniform" liked={liked.has(it.id) || it.liked} onLike={onLike} onOpen={open} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  const safeValue = Number.isFinite(value) ? value : 0;
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "var(--ink-3)" }}>
        <Icon name={icon} size={14} /> <span style={{ fontSize: 12 }}>{label}</span>
      </div>
      <div className="tnum" style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>
        {safeValue >= 1000 ? (safeValue / 1000).toFixed(1) + "k" : safeValue}
      </div>
    </div>
  );
}

function PromptCard({ title, copyLabel, copiedLabel, copied, prompt, tags, lang, onCopy }) {
  const [expanded, setExpanded] = React.useState(false);
  const canCollapse = (prompt || "").length > 520;
  React.useEffect(() => { setExpanded(false); }, [prompt]);

  return (
    <div className="card detail-prompt-card">
      <div className="detail-prompt-head">
        <span className="detail-prompt-title">
          <Icon name="edit" size={16} style={{ color: "var(--accent)" }} /> {title}
        </span>
        <div className="detail-prompt-actions">
          {canCollapse && (
            <button
              className="btn btn-ghost btn-icon detail-prompt-toggle"
              onClick={() => setExpanded((value) => !value)}
              aria-label={expanded ? (lang === "en" ? "Collapse prompt" : "프롬프트 작게보기") : (lang === "en" ? "Expand prompt" : "프롬프트 크게보기")}
              title={expanded ? (lang === "en" ? "Collapse" : "작게보기") : (lang === "en" ? "Expand" : "크게보기")}
            >
              <Icon name={expanded ? "minimize" : "maximize"} size={16} />
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onCopy} style={{ height: 30, color: copied ? "var(--ok)" : "var(--ink-2)" }}>
            {copied ? <><Icon name="check" size={15} /> {copiedLabel}</> : <><Icon name="copy" size={15} /> {copyLabel}</>}
          </button>
        </div>
      </div>
      <div className={`detail-prompt-body${expanded || !canCollapse ? " is-expanded" : ""}${canCollapse ? " is-collapsible" : ""}`}>
        <p className="detail-prompt-text">{prompt}</p>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 14 }}>
        {tags.map((tg) => (
          <span key={tg} className="chip" style={{ height: 28, fontSize: 12.5, cursor: "default" }}># {tagLabel(tg, lang)}</span>
        ))}
      </div>
    </div>
  );
}

Object.assign(window, { Detail, Stat, PromptCard });
